import { useEffect } from 'react';

export default function TerminosPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h1 className="text-3xl font-light tracking-tight text-zinc-100 mb-8 font-[Outfit]">
          Términos y Condiciones
        </h1>
        <p className="text-xs text-zinc-500 mb-8">Última actualización: Abril 2026</p>

        <div className="prose-legal space-y-8 text-sm text-zinc-400 leading-relaxed">
          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">1. Naturaleza de la plataforma</h2>
            <p>MorphMX es una plataforma intermediaria que conecta criadores, vendedores y compradores de animales. MorphMX <strong className="text-zinc-300">no es vendedor directo</strong> de ningún animal ni producto listado. Cada vendedor es responsable de la veracidad de su información, la legalidad de sus ejemplares y el cumplimiento de las transacciones.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">2. Requisitos de uso</h2>
            <p>Para usar MorphMX debes ser mayor de 18 años y tener capacidad legal para celebrar contratos. Al registrarte, aceptas proporcionar información veraz y mantenerla actualizada.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">3. Obligaciones del vendedor</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Garantizar la legal posesión de cada ejemplar ofertado.</li>
              <li>Proporcionar documentación legal válida cuando sea requerida (facturas, certificados UMA/PIMVS, documentos CITES).</li>
              <li>Describir con precisión cada ejemplar: especie, morph, sexo, edad, estado de salud.</li>
              <li>Cumplir con la legislación mexicana aplicable: NOM-059-SEMARNAT, Ley General de Vida Silvestre, CITES.</li>
              <li>No ofertar especies prohibidas, protegidas sin documentación, o de origen ilegal.</li>
              <li>Cumplir los compromisos de venta, subasta y apartado.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">4. Responsabilidad exclusiva del vendedor</h2>
            <p className="mb-3">Al registrarse como vendedor y aceptar las declaraciones legales, el vendedor reconoce y acepta que:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Es el <strong className="text-zinc-300">único responsable legal, civil, administrativa, fiscal y penalmente</strong> por la autenticidad, legalidad y veracidad de cada publicación, ejemplar, documento, certificado, permiso, factura, pedigree, carta de cesión o evidencia subida a MorphMX.</li>
              <li>Asume responsabilidad por cualquier incumplimiento, falsedad documental, posesión ilegal o comercialización restringida derivada de sus publicaciones o transacciones.</li>
              <li>Es responsable de garantizar la autenticidad documental de todo certificado, permiso o factura subido a la plataforma.</li>
              <li>Es responsable por el estado de salud y la condición declarada de cada ejemplar ofertado.</li>
              <li>Es responsable por cumplir los acuerdos de entrega, envío y pago establecidos con compradores.</li>
              <li>Asume la responsabilidad por cualquier subasta simulada (shill bidding) o manipulación de resultados.</li>
              <li>La presentación de documentos falsos, alterados o fraudulentos resultará en suspensión inmediata, conservación de evidencia y posible cooperación con autoridades.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">5. Obligaciones del comprador</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Respetar los compromisos de compra, reserva y puja.</li>
              <li>Completar pagos acordados en los tiempos establecidos.</li>
              <li>No realizar pujas fraudulentas ni manipular subastas.</li>
              <li>Reportar de buena fe cualquier irregularidad.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">6. Especies prohibidas y restringidas</h2>
            <p>Queda estrictamente prohibida la publicación de animales cuya posesión, transporte o venta viole la legislación mexicana o tratados internacionales. MorphMX se reserva el derecho de remover cualquier listado que no cumpla con las normas aplicables y cooperará con las autoridades competentes cuando sea requerido.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">7. Facultades de MorphMX</h2>
            <p className="mb-3">MorphMX, en ejercicio de su facultad como plataforma intermediaria tecnológica, podrá:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Suspender temporal o permanentemente cuentas de usuarios que incumplan estos términos.</li>
              <li>Congelar o remover listados que presenten indicios de ilegalidad, fraude o incumplimiento.</li>
              <li>Congelar pagos pendientes de liberación (payouts) cuando existan disputas abiertas o sospechas de fraude.</li>
              <li>Bloquear el acceso a funcionalidades específicas (compras, pujas, ventas) como medida preventiva.</li>
              <li>Conservar logs, registros de auditoría, documentos y evidencia digital por el tiempo que la legislación aplicable requiera.</li>
              <li>Entregar evidencia a autoridad competente (SEMARNAT, PROFEPA, Ministerio Público, autoridades fiscales) cuando sea legalmente requerido o cuando existan indicios razonables de actividad ilícita.</li>
              <li>Cancelar badges de verificación cuando las condiciones de otorgamiento dejen de cumplirse.</li>
              <li>Revocar el estatus de Criador Verificado o Criador Documentado por incumplimiento, disputas graves o fraude.</li>
              <li>Modificar o actualizar estos términos, notificando a los usuarios a través de la plataforma.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">8. Naturaleza de intermediación</h2>
            <p>MorphMX opera como plataforma intermediaria tecnológica entre usuarios. No actúa como vendedor directo, propietario del ejemplar, importador ni garante de la legalidad de los bienes ofertados por terceros. Toda transacción es responsabilidad directa de las partes involucradas. El uso indebido, fraudulento o ilícito de la plataforma será de exclusiva responsabilidad del usuario infractor.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">9. Suscripciones y pagos</h2>
            <p>MorphMX ofrece planes de suscripción para vendedores. Los pagos se procesan a través de Stripe. Las suscripciones se renuevan automáticamente al final de cada periodo. Si el pago falla, la cuenta será degradada al plan gratuito y los anuncios excedentes serán pausados (no eliminados).</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">10. Subastas y compromisos</h2>
            <p>Las pujas en subastas son compromisos vinculantes. El ganador de una subasta se compromete a completar el pago. El incumplimiento puede resultar en strikes, restricción de compras futuras o suspensión de cuenta.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">11. Sistema de strikes</h2>
            <p>MorphMX implementa un sistema de tres strikes: (1) advertencia, (2) bloqueo temporal, (3) suspensión. Los strikes se emiten por incumplimiento, fraude o violación de estas condiciones.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">12. Compra Protegida</h2>
            <p>MorphMX ofrece un servicio opcional de Compra Protegida donde actúa como intermediario de pago. Se cobra una comisión configurable. Los fondos se retienen hasta confirmación de recepción satisfactoria o resolución de disputas.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">13. Badges y reputación</h2>
            <p>Los badges de verificación se otorgan por mérito y cumplimiento. No se pueden comprar. MorphMX se reserva el derecho de revocar badges cuando las condiciones de otorgamiento dejen de cumplirse.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">14. Limitación de responsabilidad</h2>
            <p>MorphMX no garantiza la calidad, salud, genética ni legalidad de los animales listados. Toda transacción es responsabilidad directa de las partes involucradas. MorphMX facilita herramientas de confianza pero no sustituye la diligencia del comprador.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">15. Cooperación con autoridades</h2>
            <p>MorphMX cooperará con SEMARNAT, PROFEPA, y cualquier autoridad competente que requiera información sobre actividades sospechosas relacionadas con fauna silvestre.</p>
          </section>

          <section>
            <h2 className="text-lg text-zinc-200 mb-3 font-medium">16. Jurisdicción</h2>
            <p>Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia será sometida a los tribunales competentes de la Ciudad de México.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
